import React, { useContext } from 'react'
import { useApolloClient } from '@apollo/client'
import { Button, View } from 'oio-react'
import PropTypes from 'prop-types'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { NavLink } from 'react-router-dom'
import { AddIcon } from 'assets/icons'
import Title from 'src/apps/CustomApp/components/Title'
import ListMenuButton from 'src/apps/Global/components/ListMenuButton'
import NotebookContext from 'src/libraries/contexts/Notebook'
import ProtoThemeContext from 'src/libraries/contexts/ProtoTheme'
import TOCContext from 'src/libraries/contexts/TOC'
import { useNoteList, useUpdateNoteExperimental } from 'src/graphql/hooks'
import getNoteList from 'src/graphql/queries/getNoteList.gql'

const noteListLimit = 25

const NoteList = ({ notebookSlug }) => {
   const client = useApolloClient()
   const { currentNotebook, setVisibleNoteIds, visibleNoteIds } = useContext(NotebookContext)
   const { setTocIsOpen } = useContext(TOCContext)
   const { protoTheme: theme } = useContext(ProtoThemeContext)

   const buttonProps = {
      activeBackgroundColor: theme.tmpMainMenuButtonActiveBackgroundColor,
      activeTextColor: theme.tmpMainMenuButtonActiveTextColor,
      borderStyle: `1px solid ${theme.tmpMainMenuLineColor}`,
      height: theme.tmpSecondaryButtonHeight,
      textColor: theme.tmpMainMenuButtonTextColor,
      textFontWeight: theme.tmpPrimaryButtonTextFontWeight,
      textSize: theme.tmpSecondaryButtonTextSize,
      textTransform: theme.tmpPrimaryButtonTextTransform
   }

   if (!currentNotebook) {
      return null
   }

   const { updateNote } = useUpdateNoteExperimental()
   const { noteList, fetchMore, loading: noteListLoading } = useNoteList({
      initiativeId: currentNotebook?.id,
      limit: noteListLimit
   }, { skip: !currentNotebook?.id })

   const handleLoadMore = () => {
      fetchMore({
         variables: {
            start: noteList.listInfo.nextCursor
         },
         updateQuery: (prevResult, { fetchMoreResult }) => {
            if (!fetchMoreResult) {
               return prevResult
            }

            return {
               ...prevResult,
               noteList: {
                  ...prevResult.noteList,
                  items: [
                     ...prevResult.noteList.items,
                     ...fetchMoreResult.noteList.items
                  ],
                  listInfo: fetchMoreResult.noteList.listInfo
               }
            }
         }
      })
   }

   const handleDragEnd = (result) => {
      const noteId = result.draggableId
      const prevIndex = result.source.index
      const newIndex = result.destination.index

      try {
         const queryresult = client.readQuery({
            query: getNoteList,
            variables: {
               initiativeId: currentNotebook.id,
               limit: noteListLimit
            }
         })

         const newNoteItems = [...queryresult.noteList.items]
         const noteItemToMove = newNoteItems.splice(prevIndex, 1)[0]
         newNoteItems.splice(newIndex, 0, noteItemToMove)

         client.writeQuery({
            query: getNoteList,
            variables: {
               initiativeId: currentNotebook.id,
               limit: noteListLimit
            },
            data: {
               ...queryresult,
               noteList: {
                  ...queryresult.noteList,
                  items: newNoteItems
               }
            }
         })
      } catch (err) {
         // TODO: Cache error handling
         // See: #699
         // console.log(err)
      }

      updateNote(
         { id: noteId },
         { order: newIndex },
         { refetchQueries: ['getNoteList'] }
      )
   }

   return (
      <>
         <View
            width="100%"
            height="calc(100% - 55px)"
            scroll="on">
            <View
               display="flex"
               justifyContent="space-between"
               paddingVertical={theme.tmpSpacingTop}
               paddingLeft={theme.tmpSpacing}
               paddingRight="10px"
               height={theme.tmpHeadingBlockHeight}>
               <View height="30px">
                  <Title
                     size="md"
                     color={theme.tmpMainMenuTitleColor}>
                     {currentNotebook.name}
                  </Title>
               </View>
            </View>
            <View
               width="100%"
               onClick={() => {
                  setVisibleNoteIds([])
                  setTocIsOpen(false)
               }}
               paddingRight="10px"
               paddingLeft={theme.tmpSpacing}>
               <View paddingRight="5px">
                  <View borderTop={`1px solid ${theme.tmpMainMenuLineColor}`} />
               </View>
               <ListMenuButton
                  linkTo={`/notebook/${currentNotebook.slug}/messages/${currentNotebook.thread.id}`}
                  name="Messages"
                  showBadge={currentNotebook.thread.hasUnreadEntries}
                  {...buttonProps}
               />
               <ListMenuButton
                  linkTo={`/notebook/${currentNotebook.slug}/activity`}
                  name="Activity"
                  {...buttonProps}
               />
            </View>
            <View
               width="100%"
               onClick={() => setTocIsOpen(false)}
               paddingRight="10px"
               paddingLeft={theme.tmpSpacing}
               style={{ overflow: 'hidden' }}>
               <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="droppable">
                     {(provided, snapshot) => (
                        <div
                           {...provided.droppableProps}
                           ref={provided.innerRef}>
                           {noteList.items.map((note, index) => (
                              <Draggable key={note.id} draggableId={note.id} index={index}>
                                 {(itemProvided, itemSnapshot) => (
                                    <div
                                       ref={itemProvided.innerRef}
                                       {...itemProvided.draggableProps}
                                       {...itemProvided.dragHandleProps}
                                       style={{
                                          ...itemProvided.draggableProps.style,
                                          left: '0px !important'
                                       }}>
                                       <ListMenuButton
                                          key={note.id}
                                          active={visibleNoteIds.includes(note.id)}
                                          linkTo={`/notebook/${note.initiative.slug}/${note.id}`}
                                          name={note.title}
                                          pinned={note.pinned}
                                          {...buttonProps}
                                       />
                                    </div>
                                 )}
                              </Draggable>
                           ))}
                           {provided.placeholder}
                        </div>
                     )}
                  </Droppable>
               </DragDropContext>
            </View>
            {noteList.listInfo.hasNext && (
               <View
                  float="left"
                  width="100%"
                  display="flex"
                  justifyContent="center"
                  padding="12px 0px">
                  <Button
                     color="#eee"
                     textColor="#000"
                     size="xs"
                     onClick={handleLoadMore}
                     name="Load More"
                     mode={noteListLoading ? 'loading' : 'normal'}
                     rounded
                  />
               </View>
            )}
         </View>
         <View
            paddingLeft={theme.tmpSpacing}
            paddingRight="10px">
            <View
               display="flex"
               justifyContent="flex-end"
               alignItems="flex-start"
               paddingTop="10px"
               width="100%"
               height="55px"
               borderTop={`1px solid ${theme.tmpMainMenuLineColor}`}>
               {/*
               TODO: This route should not be hard-coded. Temporary until we have more
               permanent routes figured out.
               See PR #4
               */}
               <NavLink
                  to={`/notebook/${notebookSlug}/create`}
                  onClick={() => setTocIsOpen(false)}>
                  <View
                     flex="0 0 auto"
                     display="flex"
                     justifyContent="center"
                     alignItems="center"
                     width="25px"
                     height="30px"
                     borderRadius="50%">
                     <AddIcon
                        width="18px"
                        height="18px"
                        color="#666"
                        strokeWidth="2px"
                     />
                  </View>
               </NavLink>
            </View>
         </View>
      </>
   )
}

NoteList.propTypes = {
   notebookSlug: PropTypes.string.isRequired
}

export default NoteList
